'use client';

import { createClient } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────

export type BookingStatus = 'pending' | 'confirmed' | 'in-progress' | 'completed' | 'cancelled' | 'no-show';
export type PaymentStatus = 'pending' | 'succeeded' | 'failed' | 'canceled';
export type PaymentType = 'deposit' | 'full';

export interface DbBooking {
  id: string;
  user_id: string | null;
  stripe_payment_intent_id: string | null;
  stripe_customer_id: string | null;
  stripe_charge_id: string | null;
  payment_status: PaymentStatus;
  payment_type: PaymentType;
  amount_paid: number;
  service_name: string;
  vehicle_year: string | null;
  vehicle_make: string | null;
  vehicle_model: string | null;
  vehicle_color: string | null;
  appointment_date: string | null;
  appointment_time: string | null;
  addons: string[];
  notes: string | null;
  customer_first_name: string;
  customer_last_name: string;
  customer_email: string;
  customer_phone: string | null;
  booking_status: BookingStatus;
  confirmed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface Booking {
  id: string;
  bookingRef: string;
  client: string;
  email: string;
  phone: string;
  service: string;
  vehicle: string;
  vehicleMake: string;
  vehicleModel: string;
  vehicleYear: string;
  vehicleColor: string;
  date: string;
  time: string;
  status: BookingStatus;
  paymentStatus: PaymentStatus;
  paymentType: PaymentType;
  amount: number;
  deposit: number;
  addons: string[];
  notes: string;
  createdAt: string;
}

export interface DbClient {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  total_bookings: number;
  total_spent: number;
  last_booking_date: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface DbService {
  id: string;
  name: string;
  category: string;
  tagline: string | null;
  features: string[];
  base_price: number;
  duration_hours: number;
  is_popular: boolean;
  is_active: boolean;
  created_at: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isSchemaError(error: any): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '23') return false;
    if (errorClass === '08') return true;
  }
  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /type.*does not exist/i,
    ];
    return schemaErrorPatterns.some((p) => p.test(error.message));
  }
  return false;
}

function mapDbBookingToBooking(row: DbBooking): Booking {
  const depositAmount = row.payment_type === 'deposit' ? row.amount_paid : 0;
  const fullAmount = row.payment_type === 'full' ? row.amount_paid : 0;
  return {
    id: row.id,
    bookingRef: `DB-${row.id.slice(-8).toUpperCase()}`,
    client: `${row.customer_first_name} ${row.customer_last_name}`,
    email: row.customer_email,
    phone: row.customer_phone ?? '',
    service: row.service_name,
    vehicle: `${row.vehicle_year ?? ''} ${row.vehicle_make ?? ''} ${row.vehicle_model ?? ''}`.trim(),
    vehicleMake: row.vehicle_make ?? '',
    vehicleModel: row.vehicle_model ?? '',
    vehicleYear: row.vehicle_year ?? '',
    vehicleColor: row.vehicle_color ?? '',
    date: row.appointment_date ?? '',
    time: row.appointment_time ?? '',
    status: row.booking_status ?? 'pending',
    paymentStatus: row.payment_status,
    paymentType: row.payment_type,
    amount: Number(row.amount_paid),
    deposit: depositAmount,
    addons: row.addons ?? [],
    notes: row.notes ?? '',
    createdAt: row.created_at,
  };
}

// ─── Bookings Service ─────────────────────────────────────────────────────────

export const bookingsService = {
  async getAll(): Promise<Booking[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('bookings')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Bookings fetch error:', error.message);
        return [];
      }
      return (data as DbBooking[])?.map(mapDbBookingToBooking) ?? [];
    } catch (err: any) {
      console.error('bookingsService.getAll:', err.message);
      throw err;
    }
  },

  async getByEmail(email: string): Promise<Booking[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('bookings')
        .select('*')
        .eq('customer_email', email)
        .order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Bookings by email error:', error.message);
        return [];
      }
      return (data as DbBooking[])?.map(mapDbBookingToBooking) ?? [];
    } catch (err: any) {
      console.error('bookingsService.getByEmail:', err.message);
      throw err;
    }
  },

  async updateStatus(id: string, status: BookingStatus): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase
        .from('bookings')
        .update({ booking_status: status })
        .eq('id', id);

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Update status error:', error.message);
        return false;
      }
      return true;
    } catch (err: any) {
      console.error('bookingsService.updateStatus:', err.message);
      throw err;
    }
  },

  async delete(id: string): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase
        .from('bookings')
        .delete()
        .eq('id', id);

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Delete booking error:', error.message);
        return false;
      }
      return true;
    } catch (err: any) {
      console.error('bookingsService.delete:', err.message);
      throw err;
    }
  },

  async getKpiStats(): Promise<{
    todayCount: number;
    todayConfirmed: number;
    todayInProgress: number;
    todayPending: number;
    weeklyRevenue: number;
    weeklyDeposits: number;
    weeklyFull: number;
    pendingDepositsCount: number;
    completedThisMonth: number;
    cancelledThisMonth: number;
  }> {
    const supabase = createClient();
    try {
      const now = new Date();
      const todayStr = now.toISOString().split('T')[0];
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

      const { data: allBookings, error } = await supabase
        .from('bookings')
        .select('booking_status, payment_status, payment_type, amount_paid, appointment_date, created_at')
        .gte('created_at', monthStart);

      if (error) {
        if (isSchemaError(error)) throw error;
        return {
          todayCount: 0, todayConfirmed: 0, todayInProgress: 0, todayPending: 0,
          weeklyRevenue: 0, weeklyDeposits: 0, weeklyFull: 0,
          pendingDepositsCount: 0, completedThisMonth: 0, cancelledThisMonth: 0,
        };
      }

      const bookings = allBookings ?? [];
      const todayBookings = bookings.filter((b: any) => b.appointment_date === todayStr);
      const weeklyBookings = bookings.filter((b: any) => new Date(b.created_at) >= new Date(weekAgo));

      const weeklyRevenue = weeklyBookings
        .filter((b: any) => b.payment_status === 'succeeded')
        .reduce((sum: number, b: any) => sum + Number(b.amount_paid), 0);

      const weeklyDeposits = weeklyBookings
        .filter((b: any) => b.payment_status === 'succeeded' && b.payment_type === 'deposit')
        .reduce((sum: number, b: any) => sum + Number(b.amount_paid), 0);

      const weeklyFull = weeklyBookings
        .filter((b: any) => b.payment_status === 'succeeded' && b.payment_type === 'full')
        .reduce((sum: number, b: any) => sum + Number(b.amount_paid), 0);

      return {
        todayCount: todayBookings.length,
        todayConfirmed: todayBookings.filter((b: any) => b.booking_status === 'confirmed').length,
        todayInProgress: todayBookings.filter((b: any) => b.booking_status === 'in-progress').length,
        todayPending: todayBookings.filter((b: any) => b.booking_status === 'pending').length,
        weeklyRevenue,
        weeklyDeposits,
        weeklyFull,
        pendingDepositsCount: bookings.filter((b: any) => b.payment_status === 'pending').length,
        completedThisMonth: bookings.filter((b: any) => b.booking_status === 'completed').length,
        cancelledThisMonth: bookings.filter((b: any) => b.booking_status === 'cancelled').length,
      };
    } catch (err: any) {
      console.error('bookingsService.getKpiStats:', err.message);
      throw err;
    }
  },

  async getTodayBookings(): Promise<Booking[]> {
    const supabase = createClient();
    try {
      const todayStr = new Date().toISOString().split('T')[0];
      const { data, error } = await supabase
        .from('bookings')
        .select('*')
        .eq('appointment_date', todayStr)
        .order('appointment_time', { ascending: true });

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Today bookings error:', error.message);
        return [];
      }
      return (data as DbBooking[])?.map(mapDbBookingToBooking) ?? [];
    } catch (err: any) {
      console.error('bookingsService.getTodayBookings:', err.message);
      return [];
    }
  },

  async getWeekBookings(): Promise<Booking[]> {
    const supabase = createClient();
    try {
      const now = new Date();
      // Get Monday of current week
      const day = now.getDay();
      const diffToMon = day === 0 ? -6 : 1 - day;
      const monday = new Date(now);
      monday.setDate(now.getDate() + diffToMon);
      monday.setHours(0, 0, 0, 0);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      sunday.setHours(23, 59, 59, 999);

      const monStr = monday.toISOString().split('T')[0];
      const sunStr = sunday.toISOString().split('T')[0];

      const { data, error } = await supabase
        .from('bookings')
        .select('*')
        .gte('appointment_date', monStr)
        .lte('appointment_date', sunStr)
        .order('appointment_time', { ascending: true });

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Week bookings error:', error.message);
        return [];
      }
      return (data as DbBooking[])?.map(mapDbBookingToBooking) ?? [];
    } catch (err: any) {
      console.error('bookingsService.getWeekBookings:', err.message);
      return [];
    }
  },

  async getBookingVolumeByDay(): Promise<{ day: string; bookings: number }[]> {
    const supabase = createClient();
    try {
      const now = new Date();
      const day = now.getDay();
      const diffToMon = day === 0 ? -6 : 1 - day;
      const monday = new Date(now);
      monday.setDate(now.getDate() + diffToMon);
      monday.setHours(0, 0, 0, 0);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);

      const monStr = monday.toISOString().split('T')[0];
      const sunStr = sunday.toISOString().split('T')[0];

      const { data, error } = await supabase
        .from('bookings')
        .select('appointment_date')
        .gte('appointment_date', monStr)
        .lte('appointment_date', sunStr);

      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }

      const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      const counts: Record<string, number> = {};
      dayLabels.forEach((d) => (counts[d] = 0));

      (data ?? []).forEach((b: any) => {
        if (!b.appointment_date) return;
        const d = new Date(b.appointment_date + 'T00:00:00');
        const idx = d.getDay(); // 0=Sun
        const label = dayLabels[idx === 0 ? 6 : idx - 1];
        if (label) counts[label] = (counts[label] ?? 0) + 1;
      });

      return dayLabels.map((d) => ({ day: d, bookings: counts[d] ?? 0 }));
    } catch (err: any) {
      console.error('bookingsService.getBookingVolumeByDay:', err.message);
      return [];
    }
  },

  async getRevenueTrend(): Promise<{ date: string; revenue: number }[]> {
    const supabase = createClient();
    try {
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const fromStr = thirtyDaysAgo.toISOString().split('T')[0];

      const { data, error } = await supabase
        .from('bookings')
        .select('appointment_date, amount_paid, payment_status')
        .gte('appointment_date', fromStr)
        .eq('payment_status', 'succeeded')
        .order('appointment_date', { ascending: true });

      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }

      // Group by date
      const grouped: Record<string, number> = {};
      (data ?? []).forEach((b: any) => {
        if (!b.appointment_date) return;
        grouped[b.appointment_date] = (grouped[b.appointment_date] ?? 0) + Number(b.amount_paid);
      });

      return Object.entries(grouped).map(([date, revenue]) => {
        const d = new Date(date + 'T00:00:00');
        const label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        return { date: label, revenue };
      });
    } catch (err: any) {
      console.error('bookingsService.getRevenueTrend:', err.message);
      return [];
    }
  },

  async getServiceBreakdown(): Promise<{ name: string; count: number }[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('bookings')
        .select('service_name');

      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }

      const counts: Record<string, number> = {};
      (data ?? []).forEach((b: any) => {
        if (!b.service_name) return;
        counts[b.service_name] = (counts[b.service_name] ?? 0) + 1;
      });

      return Object.entries(counts)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count);
    } catch (err: any) {
      console.error('bookingsService.getServiceBreakdown:', err.message);
      return [];
    }
  },
};

// ─── Clients Service ──────────────────────────────────────────────────────────

export const clientsService = {
  async upsertFromBooking(params: {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    amountPaid: number;
    appointmentDate?: string;
  }): Promise<void> {
    const supabase = createClient();
    try {
      // Check if client exists
      const { data: existing } = await supabase
        .from('clients')
        .select('id, total_bookings, total_spent')
        .eq('email', params.email)
        .maybeSingle();

      if (existing) {
        await supabase
          .from('clients')
          .update({
            total_bookings: (existing.total_bookings ?? 0) + 1,
            total_spent: Number(existing.total_spent ?? 0) + params.amountPaid,
            last_booking_date: params.appointmentDate ?? null,
            phone: params.phone ?? null,
          })
          .eq('id', existing.id);
      } else {
        await supabase
          .from('clients')
          .insert({
            first_name: params.firstName,
            last_name: params.lastName,
            email: params.email,
            phone: params.phone ?? null,
            total_bookings: 1,
            total_spent: params.amountPaid,
            last_booking_date: params.appointmentDate ?? null,
          });
      }
    } catch (err: any) {
      console.error('clientsService.upsertFromBooking:', err.message);
    }
  },
};

// ─── Services Service ─────────────────────────────────────────────────────────

export const servicesService = {
  async getAll(): Promise<DbService[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('services')
        .select('*')
        .eq('is_active', true)
        .order('base_price', { ascending: true });

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Services fetch error:', error.message);
        return [];
      }
      return (data as DbService[]) ?? [];
    } catch (err: any) {
      console.error('servicesService.getAll:', err.message);
      throw err;
    }
  },
};
